import type {
  CreateProductInput,
  CreateRepresentativeInput,
  CreateServiceInput,
  EventSearchResponse,
  ProductSummary,
  RepresentativeSummary,
  ServiceSummary,
  UpdateProductInput,
  UpdateRepresentativeInput,
  UpdateServiceInput,
} from "@mvk/shared";
import { apiRequest } from "./client";

type Audience = "visitor" | "admin";

export type SearchSuggestions = {
  recent: string[];
  popular: string[];
};

export const catalogApi = {
  search(eventId: string, q: string) {
    const params = new URLSearchParams({ q });
    return apiRequest<EventSearchResponse>(`/events/${eventId}/search?${params}`, {
      audience: "visitor",
    });
  },

  getSearchSuggestions(eventId: string) {
    return apiRequest<SearchSuggestions>(`/events/${eventId}/search/suggestions`, {
      audience: "visitor",
    });
  },

  recordSearchQuery(eventId: string, query: string) {
    return apiRequest<{ ok: boolean } & SearchSuggestions>(
      `/events/${eventId}/search/queries`,
      { method: "POST", body: { query }, audience: "visitor" },
    );
  },

  clearRecentSearches(eventId: string) {
    return apiRequest<{ ok: boolean; recent: string[] }>(
      `/events/${eventId}/search/queries/recent`,
      { method: "DELETE", audience: "visitor" },
    );
  },

  listProducts(eventId: string, companyId: string, audience: Audience = "visitor") {
    return apiRequest<{ products: ProductSummary[] }>(
      `/events/${eventId}/companies/${companyId}/products`,
      { audience },
    );
  },

  listRepresentatives(eventId: string, companyId: string, audience: Audience = "visitor") {
    return apiRequest<{ representatives: RepresentativeSummary[] }>(
      `/events/${eventId}/companies/${companyId}/representatives`,
      { audience },
    );
  },

  listServices(eventId: string, companyId: string, audience: Audience = "visitor") {
    return apiRequest<{ services: ServiceSummary[] }>(
      `/events/${eventId}/companies/${companyId}/services`,
      { audience },
    );
  },

  listFavoriteProducts(eventId: string) {
    return apiRequest<{ products: ProductSummary[] }>(
      `/events/${eventId}/products/favorites`,
      { audience: "visitor" },
    );
  },

  addProductFavorite(eventId: string, productId: string) {
    return apiRequest<{ favorited: boolean }>(
      `/events/${eventId}/products/${productId}/favorite`,
      { method: "POST", audience: "visitor" },
    );
  },

  removeProductFavorite(eventId: string, productId: string) {
    return apiRequest<{ favorited: boolean }>(
      `/events/${eventId}/products/${productId}/favorite`,
      { method: "DELETE", audience: "visitor" },
    );
  },

  isProductFavorite(eventId: string, productId: string) {
    return apiRequest<{ favorited: boolean }>(
      `/events/${eventId}/products/${productId}/favorite`,
      { audience: "visitor" },
    );
  },

  createProduct(eventId: string, companyId: string, body: CreateProductInput) {
    return apiRequest<{ product: ProductSummary }>(
      `/events/${eventId}/companies/${companyId}/products`,
      { method: "POST", body, audience: "admin" },
    );
  },

  updateProduct(
    eventId: string,
    companyId: string,
    productId: string,
    body: UpdateProductInput,
  ) {
    return apiRequest<{ product: ProductSummary }>(
      `/events/${eventId}/companies/${companyId}/products/${productId}`,
      { method: "PATCH", body, audience: "admin" },
    );
  },

  deleteProduct(eventId: string, companyId: string, productId: string) {
    return apiRequest<{ ok: boolean }>(
      `/events/${eventId}/companies/${companyId}/products/${productId}`,
      { method: "DELETE", audience: "admin" },
    );
  },

  createRepresentative(
    eventId: string,
    companyId: string,
    body: CreateRepresentativeInput,
  ) {
    return apiRequest<{ representative: RepresentativeSummary }>(
      `/events/${eventId}/companies/${companyId}/representatives`,
      { method: "POST", body, audience: "admin" },
    );
  },

  updateRepresentative(
    eventId: string,
    companyId: string,
    repId: string,
    body: UpdateRepresentativeInput,
  ) {
    return apiRequest<{ representative: RepresentativeSummary }>(
      `/events/${eventId}/companies/${companyId}/representatives/${repId}`,
      { method: "PATCH", body, audience: "admin" },
    );
  },

  deleteRepresentative(eventId: string, companyId: string, repId: string) {
    return apiRequest<{ ok: boolean }>(
      `/events/${eventId}/companies/${companyId}/representatives/${repId}`,
      { method: "DELETE", audience: "admin" },
    );
  },

  createService(eventId: string, companyId: string, body: CreateServiceInput) {
    return apiRequest<{ service: ServiceSummary }>(
      `/events/${eventId}/companies/${companyId}/services`,
      { method: "POST", body, audience: "admin" },
    );
  },

  updateService(
    eventId: string,
    companyId: string,
    serviceId: string,
    body: UpdateServiceInput,
  ) {
    return apiRequest<{ service: ServiceSummary }>(
      `/events/${eventId}/companies/${companyId}/services/${serviceId}`,
      { method: "PATCH", body, audience: "admin" },
    );
  },

  deleteService(eventId: string, companyId: string, serviceId: string) {
    return apiRequest<{ ok: boolean }>(
      `/events/${eventId}/companies/${companyId}/services/${serviceId}`,
      { method: "DELETE", audience: "admin" },
    );
  },
};
